import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useVehicles } from '../../../hooks';
import styles from './InventoryDetail.module.css';
import moment from 'moment';
import logo from '../../../assets/images/logoConcesionario.png';
import { InstagramEmbed, FacebookEmbed, TwitterEmbed, YouTubeEmbed, TikTokEmbed, LinkedInEmbed } from 'react-social-media-embed';

export const InventoryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { vehicles, isLoading, loadVehicles } = useVehicles();

  // Buscar el vehículo por ID
  const vehicle = vehicles.find((v) => v.id === id);

  useEffect(() => {
    // Si no hay vehículos, intentar cargarlos
    if (vehicles.length === 0) {
      loadVehicles();
    }
  }, [vehicles.length, loadVehicles]);

  useEffect(() => {
    // Si después de cargar no existe el vehículo, redirigir
    if (!isLoading && vehicles.length > 0 && !vehicle) {
      toast.error('Vehículo no encontrado');
      navigate('/inventory');
    }
  }, [isLoading, vehicles.length, vehicle, navigate]);

  const copyToClipboard = async (value: string | number, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success(`${fieldName} copiado al portapapeles`, {
        icon: '📋',
        duration: 2000,
      });
    } catch (error) {
      toast.error('Error al copiar al portapapeles');
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency === 'USD' ? 'USD' : 'ARS',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return moment(date).format('DD/MM/YYYY');
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Cargando información del vehículo...</p>
      </div>
    );
  }

  if (!vehicle) {
    return null; // El useEffect se encargará de redirigir
  } else {
    console.log({ vehicle });
  }

  const InfoField: React.FC<{ label: string; value: string | number; fieldName: string }> = ({
    label,
    value,
    fieldName,
  }) => (
    <div className={styles.infoField}>
      <label className={styles.label}>{label}</label>
      <div className={styles.valueContainer}>
        <span className={styles.value}>{value}</span>
        <button
          className={styles.copyButton}
          onClick={() => copyToClipboard(value, fieldName)}
          title={`Copiar ${fieldName}`}
        >
          📋
        </button>
      </div>
    </div>
  );

  const LinkInfoField: React.FC<{ label: string; value: string; fieldName: string }> = ({
    label,
    value,
    fieldName,
  }) => (
    <div className={styles.infoField}>
      <label className={styles.label}>{label}</label>
      <div className={styles.valueContainer}>
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.value}
          style={{ color: '#00A6E8', textDecoration: 'underline' }}
        >
          {value}
        </a>
        <button
          className={styles.copyButton}
          onClick={() => copyToClipboard(value, fieldName)}
          title={`Copiar ${fieldName}`}
        >
          📋
        </button>
      </div>
    </div>
  );

  const SocialMediaEmbed: React.FC<{ url: string }> = ({ url }) => {
    if (!url) return null;

    const getEmbedComponent = () => {
      if (url.includes('instagram.com')) {
        return <InstagramEmbed url={url} width={328} />;
      }
      if (url.includes('facebook.com')) {
        return <FacebookEmbed url={url} width={328} />;
      }
      if (url.includes('twitter.com') || url.includes('x.com')) {
        return <TwitterEmbed url={url} width={328} />;
      }
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        return <YouTubeEmbed url={url} width={328} />;
      }
      if (url.includes('tiktok.com')) {
        return <TikTokEmbed url={url} width={328} />;
      }
      if (url.includes('linkedin.com')) {
        return <LinkedInEmbed url={url} width={328} />;
      }
      return null;
    };

    const embedComponent = getEmbedComponent();

    if (!embedComponent) return null;

    return (
      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
        {embedComponent}
      </div>
    );
  };

  return (
    <div className={styles.detailContainer}>
      {/* Logo para impresión */}
      <div className={styles.printLogo}>
        <img src={logo} alt="El 4+2 Motos" />
      </div>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to="/inventory" className={styles.backButton}>
            ← Volver al inventario
          </Link>
          <h1 className={styles.title}>
            {vehicle.brand} {vehicle.model}
          </h1>
          <div className={styles.badges}>
            <span className={`${styles.badge} ${styles[vehicle.condition]}`}>
              {vehicle.condition === 'nuevo' ? '🆕 Nuevo' : '🔄 Usado'}
            </span>
            <span className={styles.badge}>{vehicle.vehicleType}</span>
          </div>
        </div>
        <div className={styles.headerRight}>
          <Link to={`/inventory/edit/${vehicle.id}`} className={styles.editButton}>
            ✏️ Editar
          </Link>
        </div>
      </div>

      {/* Título para impresión */}
      <div className={styles.printTitle}>
        <h1>{vehicle.brand} {vehicle.model}</h1>
        <div className={styles.printBadges}>
          <span className={vehicle.condition === 'nuevo' ? styles.printBadgeNew : styles.printBadgeUsed}>
            {vehicle.condition === 'nuevo' ? 'Nuevo' : 'Usado'}
          </span>
          <span className={styles.printBadgeType}>{vehicle.vehicleType}</span>
        </div>
      </div>

      {/* Secciones de Información */}
      <div className={styles.sections}>
        {/* Imagen del Vehículo */}
        {vehicle.vehicleImageLink && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>🖼️ Imagen del Vehículo</h2>
            <div className={styles.imageContainer}>
              <img
                src={vehicle.vehicleImageLink}
                alt={`${vehicle.brand} {vehicle.model}`}
                className={styles.vehicleImage}
                onError={(e) => {
                  // Si la imagen falla al cargar, ocultar el elemento
                  e.currentTarget.style.display = 'none';
                  const container = e.currentTarget.parentElement;
                  if (container) {
                    const errorMsg = document.createElement('p');
                    errorMsg.textContent = 'No se pudo cargar la imagen';
                    errorMsg.className = styles.imageError;
                    container.appendChild(errorMsg);
                  }
                }}
              />
            </div>
          </section>
        )}

        {/* Información Básica */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>📋 Información Básica</h2>
          <div className={styles.grid}>
            <InfoField label="Marca" value={vehicle.brand} fieldName="Marca" />
            <InfoField label="Modelo" value={vehicle.model} fieldName="Modelo" />
            <InfoField label="Tipo" value={vehicle.vehicleType} fieldName="Tipo" />
            <InfoField label="Año" value={vehicle.year} fieldName="Año" />
            <InfoField
              label="Kilómetros"
              value={vehicle.mileage.toLocaleString('es-AR')}
              fieldName="Kilómetros"
            />
            <InfoField label="Color" value={vehicle.color} fieldName="Color" />
            <InfoField
              label="Condición"
              value={vehicle.condition === 'nuevo' ? 'Nuevo' : 'Usado'}
              fieldName="Condición"
            />
          </div>
        </section>

        {/* Identificación Técnica */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>🔧 Identificación Técnica</h2>
          <div className={styles.grid}>
            <InfoField label="Nº de Chasis" value={vehicle.vin} fieldName="Chasis" />
            <InfoField label="Nº de Motor" value={vehicle.engine} fieldName="Motor" />
            <InfoField label="Dominio/Patente" value={vehicle.licensePlate || '-'} fieldName="Dominio" />
          </div>
        </section>

        {/* Información Comercial */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>💰 Información Comercial</h2>
          <div className={styles.grid}>
            <InfoField
              label="Precio"
              value={formatCurrency(vehicle.price, vehicle.currency)}
              fieldName="Precio"
            />
            <InfoField label="Moneda" value={vehicle.currency} fieldName="Moneda" />
            <InfoField
              label="Fecha de Ingreso"
              value={vehicle.entryDate ? formatDate(vehicle.entryDate) : '-'}
              fieldName="Fecha de Ingreso"
            />
            <InfoField
              label="Fecha de Registro"
              value={formatDate(vehicle.createdAt)}
              fieldName="Fecha de Registro"
            />
            {vehicle.saleDate && (
              <InfoField
                label="Fecha de Venta"
                value={formatDate(vehicle.saleDate)}
                fieldName="Fecha de Venta"
              />
            )}
            {vehicle.publicationLink && (
              <LinkInfoField
                label="Link Publicación"
                value={vehicle.publicationLink}
                fieldName="Link Publicación"
              />
            )}
          </div>
        </section>

        {/* Observaciones */}
        {vehicle.description && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>📝 Observaciones</h2>
            <div className={styles.observationContainer}>
              <div className={styles.observationText}>{vehicle.description}</div>
              <button
                className={styles.copyObservationButton}
                onClick={() => copyToClipboard(vehicle.description || '', 'Observación')}
              >
                📋 Copiar observación
              </button>
            </div>
          </section>
        )}
        {vehicle.publicationLink && (
          <div>
            <h2 className={styles.sectionTitle}>Previsualización de publicación</h2>
            <SocialMediaEmbed url={vehicle.publicationLink} />
          </div>
        )}
      </div>

      {/* Acciones del footer */}
      <div className={styles.footer}>
        <Link to="/inventory" className={styles.backButtonFooter}>
          Volver al inventario
        </Link>
        <div className={styles.footerActions}>
          <button className={styles.printButton} onClick={() => window.print()}>
            🖨️ Imprimir
          </button>
          <Link to={`/inventory/edit/${vehicle.id}`} className={styles.editButtonFooter}>
            ✏️ Editar vehículo
          </Link>
        </div>
      </div>
    </div>
  );
};

